// Detection testing (F-HQ-16, spec/14 §6): paste a station's process list — names or full image paths, one per line;
// a `tasklist /fo csv` dump works — and see which catalogue titles each process would be detected as, and by which
// rule: the title's own rules (process name, path pattern, regex — every rule set must hold), its launch program when
// it has none, or the curated suggested names. The matching runs on the api with the station's rules
// (`POST /v1/hq/catalogue/detection/test`); nothing is written.

import type { DetectionTestResult } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Dialog, Field } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { Notice } from '../../ui';
import { useDetectionTest } from './queries';
import { useProblem } from '../../api/use-problem';

export function DetectionTestDialog({ open, onOpenChange }: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" title={t('catalogue.detectionTest.title')} description={t('catalogue.detectionTest.intro')}>
      {open ? <DetectionTestForm /> : null}
    </Dialog>
  );
}

function DetectionTestForm(): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const test = useDetectionTest();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const run = () => {
    if (text.trim() === '') {
      setError(t('catalogue.detectionTest.empty'));
      return;
    }
    setError(undefined);
    test.mutate(text, { onError: (err) => setError(problems.text(err, { fallback: t('catalogue.detectionTest.failed') })) });
  };

  return (
    <div className="console-form">
      <Field label={t('catalogue.detectionTest.label')} description={t('catalogue.detectionTest.hint')} error={error}>
        {(control) => (
          <textarea
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            className="clav-input console-detection-test__input"
            rows={8}
            spellCheck={false}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </Field>
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="primary" busy={test.isPending} onClick={run}>
          {t('catalogue.detectionTest.run')}
        </Button>
      </div>
      {test.data === undefined ? null : <DetectionTestResults result={test.data} />}
    </div>
  );
}

/** Each process and the titles it matched, matched processes first; the counts in one line above. */
export function DetectionTestResults({ result }: { readonly result: DetectionTestResult }): ReactNode {
  const { t } = useTranslation('hq');
  const lines = [...result.lines].sort((a, b) => Number(b.matches.length > 0) - Number(a.matches.length > 0));
  return (
    <div className="console-detection-test__results">
      <p role="status" className="console-muted">
        {result.matchedTitles === 0 ? t('catalogue.detectionTest.matchedNone') : t('catalogue.detectionTest.summary', { count: result.matchedTitles, processes: result.lines.length })}
      </p>
      {result.matchedTitles === 0 && result.lines.length > 0 ? <Notice tone="info">{t('catalogue.detectionTest.noneHint')}</Notice> : null}
      <ul className="console-detection-test__list" aria-label={t('catalogue.detectionTest.caption')}>
        {lines.map((line, index) => (
          <li key={`${line.input}-${index}`} className="console-detection-test__row">
            <code className="console-detection-test__process" title={line.path ?? undefined}>
              {line.processName}
            </code>
            {line.matches.length === 0 ? (
              <span className="console-muted">{t('catalogue.detectionTest.noMatch')}</span>
            ) : (
              <span className="console-detection-test__matches">
                {line.matches.map((match) => (
                  <span key={match.gameId} className="console-detection-test__match">
                    <strong>{match.name}</strong>
                    <span className="console-muted">
                      {t('catalogue.detectionTest.matchDetail', {
                        via: t(`catalogue.detectionTest.via.${match.via}`),
                        rules: match.rules.map((rule) => t(`catalogue.detectionTest.rule.${rule}`)).join(' + '),
                      })}
                    </span>
                    {match.archived ? <Badge tone="warn">{t('catalogue.card.archived')}</Badge> : null}
                  </span>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
